import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../services/api';
import { useApp } from '../context/AppContext';
import { Badge } from '../components/Badge';
import { StatCard } from '../components/StatCard';
import { formatDate } from '../utils/format';
import { DEMO_NOTIFICATIONS, DEMO_RRHH_TASKS } from '../data/commsNotificationsDemo';

const SUB_TABS = ['notifications', 'tasks'];

const PRE_CLIENT_KEYS = ['preClient1', 'preClient2', 'preClient3', 'preClient4', 'preClient5', 'preClient6', 'preClient7'];

function priorityVariant(p) {
  if (p === 'high') return 'error';
  if (p === 'medium') return 'pending';
  return 'approved';
}

function notifStatusVariant(s) {
  if (s === 'completed') return 'approved';
  if (s === 'overdue') return 'rejected';
  if (s === 'in_progress') return 'pending';
  return 'draft';
}

function NotifRows({ items, t, markDone, postpone }) {
  if (!items.length) {
    return <p className="panel__muted text-sm">{t('notifications.emptySection')}</p>;
  }
  return (
    <ul className="notif-list notif-list--compact">
      {items.map((n) => (
        <li key={n.id} className="notif-row panel panel--elevated">
          <div className="notif-row__main">
            <div className="notif-row__badges">
              <Badge variant={priorityVariant(n.priority)}>{t(`notifications.priority_${n.priority}`)}</Badge>
              <Badge variant={notifStatusVariant(n.status)}>{t(`notifications.status_${n.status}`)}</Badge>
              <span className="panel__muted text-sm">{t(`settings.${n.moduleKey}`)}</span>
            </div>
            <p className="notif-row__title">{n.titleKey ? t(`notifications.${n.titleKey}`) : n.title}</p>
            <p className="panel__muted text-sm">{t('notifications.due')}: {formatDate(n.due)}</p>
          </div>
          <div className="notif-row__actions">
            <button type="button" className="btn btn--sm btn--secondary" onClick={() => markDone(n.id)} disabled={n.status === 'completed'}>
              {t('notifications.actionComplete')}
            </button>
            <button type="button" className="btn btn--sm btn--ghost" onClick={() => postpone(n.id)}>
              {t('notifications.actionSnooze')}
            </button>
            <Link to="/records" className="btn btn--sm btn--ghost">
              {t('notifications.actionOrigin')}
            </Link>
          </div>
        </li>
      ))}
    </ul>
  );
}

function TaskRows({ items }) {
  const { t } = useApp();
  if (!items.length) {
    return <p className="panel__muted text-sm">{t('notifications.emptySection')}</p>;
  }
  return (
    <div className="table-wrap table-wrap--rounded">
      <table className="data-table notif-task-table">
        <thead>
          <tr>
            <th>{t('notifications.colTask')}</th>
            <th>{t('notifications.colOwner')}</th>
            <th>{t('notifications.colModule')}</th>
            <th>{t('notifications.colDue')}</th>
            <th>{t('notifications.colPriority')}</th>
            <th>{t('notifications.colStatus')}</th>
          </tr>
        </thead>
        <tbody>
          {items.map((tk) => (
            <tr key={tk.id}>
              <td>{t(`notifications.${tk.titleKey}`)}</td>
              <td>{tk.owner}</td>
              <td>{t(`settings.${tk.moduleKey}`)}</td>
              <td className="mono">{formatDate(tk.due)}</td>
              <td>
                <Badge variant={priorityVariant(tk.priority)}>{t(`notifications.priority_${tk.priority}`)}</Badge>
              </td>
              <td>
                <Badge variant={notifStatusVariant(tk.status)}>{t(`notifications.status_${tk.status}`)}</Badge>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function Notifications() {
  const { t, isProductionUi } = useApp();
  const [sub, setSub] = useState('notifications');
  const [localDone, setLocalDone] = useState({});
  const [apiNotifs, setApiNotifs] = useState([]);

  useEffect(() => {
    api
      .get('/api/notifications')
      .then((r) => setApiNotifs(Array.isArray(r.data) ? r.data : []))
      .catch(() => setApiNotifs([]));
  }, []);

  const notifs = useMemo(() => {
    if (isProductionUi) {
      return apiNotifs.map((n) => ({
        id: n.id,
        priority: n.priority || 'medium',
        status: n.status === 'read' ? 'completed' : 'pending',
        moduleKey: n.module || 'rrhh',
        titleKey: null,
        title: n.title,
        due: n.created_at,
      }));
    }
    return DEMO_NOTIFICATIONS.map((n) => ({
      ...n,
      status: localDone[n.id] || n.status,
    }));
  }, [localDone, isProductionUi, apiNotifs]);

  const tasks = useMemo(() => {
    return DEMO_RRHH_TASKS.map((tk) => ({
      ...tk,
      status: localDone[tk.id] || tk.status,
    }));
  }, [localDone]);

  const kpis = useMemo(() => {
    const pend = notifs.filter((n) => n.status === 'pending').length;
    const overdue = notifs.filter((n) => n.status === 'overdue').length;
    const crit = notifs.filter((n) => n.priority === 'high' && n.status !== 'completed').length;
    const done = notifs.filter((n) => n.status === 'completed').length;
    const week = notifs.filter((n) => {
      if (!n.due) return false;
      const d = new Date(n.due);
      const now = new Date();
      const diff = (d - now) / 86400000;
      return diff >= 0 && diff <= 7;
    }).length;
    return { pend, overdue, crit, done, week };
  }, [notifs]);

  const taskKpis = useMemo(() => {
    const list = tasks;
    return {
      pend: list.filter((x) => x.status === 'pending').length,
      overdue: list.filter((x) => x.status === 'overdue').length,
      crit: list.filter((x) => x.priority === 'high' && x.status !== 'completed').length,
      done: list.filter((x) => x.status === 'completed').length,
      week: list.filter((x) => {
        const d = new Date(x.due);
        const now = new Date();
        const diff = (d - now) / 86400000;
        return diff >= 0 && diff <= 7;
      }).length,
    };
  }, [tasks]);

  const markDone = (id) => {
    setLocalDone((prev) => ({ ...prev, [id]: 'completed' }));
  };

  const postpone = (id) => {
    setLocalDone((prev) => ({ ...prev, [id]: 'in_progress' }));
  };

  const notifCritical = useMemo(() => notifs.filter((n) => n.priority === 'high' && n.status !== 'completed'), [notifs]);
  const notifOverdue = useMemo(() => notifs.filter((n) => n.status === 'overdue'), [notifs]);
  const notifUpcoming = useMemo(() => {
    const now = new Date();
    return notifs.filter((n) => {
      if (!n.due) return false;
      if (n.status === 'completed' || n.status === 'overdue') return false;
      const d = new Date(n.due);
      const diff = (d - now) / 86400000;
      return diff >= 0 && diff <= 7;
    });
  }, [notifs]);
  const notifIntegration = useMemo(() => notifs.filter((n) => n.kind === 'integration'), [notifs]);
  const notifOther = useMemo(() => {
    const ids = new Set([...notifCritical, ...notifOverdue, ...notifUpcoming, ...notifIntegration].map((n) => n.id));
    return notifs.filter((n) => !ids.has(n.id));
  }, [notifs, notifCritical, notifOverdue, notifUpcoming, notifIntegration]);

  const taskIntegration = useMemo(() => tasks.filter((x) => x.titleKey.startsWith('taskPerm')), [tasks]);
  const restTasks = useMemo(() => tasks.filter((x) => !x.titleKey.startsWith('taskPerm')), [tasks]);

  const taskCritical = useMemo(
    () => restTasks.filter((x) => x.priority === 'high' && x.status !== 'completed'),
    [restTasks]
  );
  const taskOverdue = useMemo(() => restTasks.filter((x) => x.status === 'overdue'), [restTasks]);
  const taskUpcoming = useMemo(() => {
    const now = new Date();
    return restTasks.filter((x) => {
      if (x.status === 'completed' || x.status === 'overdue') return false;
      const d = new Date(x.due);
      const diff = (d - now) / 86400000;
      return diff >= 0 && diff <= 7;
    });
  }, [restTasks]);
  const taskOther = useMemo(() => {
    const ids = new Set([...taskCritical, ...taskOverdue, ...taskUpcoming].map((x) => x.id));
    return restTasks.filter((x) => !ids.has(x.id));
  }, [restTasks, taskCritical, taskOverdue, taskUpcoming]);

  const k = sub === 'notifications' ? kpis : taskKpis;

  return (
    <div className="page-stack notifications-page notifications-page--rc">
      <header className="notif-hero panel panel--elevated">
        <h1 className="notif-hero__title">{t('notifications.title')}</h1>
        <p className="notif-hero__lead">{t('notifications.subtitle')}</p>
        <p className="panel__muted text-sm">{t('notifications.rcHint')}</p>
        <div className="comm-kpi-row grid-stats">
          <StatCard icon="◆" label={t('notifications.kpiPending')} value={k.pend} tone="warning" />
          <StatCard icon="⏱" label={t('notifications.kpiOverdue')} value={k.overdue} tone="accent" />
          <StatCard icon="!" label={t('notifications.kpiCritical')} value={k.crit} />
          <StatCard icon="✓" label={t('notifications.kpiDone')} value={k.done} tone="success" />
          <StatCard icon="◎" label={t('notifications.kpiWeek')} value={k.week} />
        </div>
      </header>

      <div className="comm-tabs panel panel--elevated" role="tablist" aria-label={t('notifications.subTabsAria')}>
        {SUB_TABS.map((id) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={sub === id}
            className={`comm-tab${sub === id ? ' is-active' : ''}`}
            onClick={() => setSub(id)}
          >
            {t(`notifications.sub_${id}`)}
          </button>
        ))}
      </div>

      {sub === 'notifications' && (
        <>
          <section className="panel panel--elevated notif-bucket">
            <h2 className="panel__title">{t('notifications.secCritical')}</h2>
            <NotifRows items={notifCritical} t={t} markDone={markDone} postpone={postpone} />
          </section>
          <section className="panel panel--elevated notif-bucket">
            <h2 className="panel__title">{t('notifications.secOverdue')}</h2>
            <NotifRows items={notifOverdue} t={t} markDone={markDone} postpone={postpone} />
          </section>
          <section className="panel panel--elevated notif-bucket">
            <h2 className="panel__title">{t('notifications.secUpcoming')}</h2>
            <NotifRows items={notifUpcoming} t={t} markDone={markDone} postpone={postpone} />
          </section>
          <section className="panel panel--elevated notif-bucket">
            <h2 className="panel__title">{t('notifications.secIntegration')}</h2>
            <NotifRows items={notifIntegration} t={t} markDone={markDone} postpone={postpone} />
          </section>
          <section className="panel panel--elevated notif-bucket">
            <h2 className="panel__title">{t('notifications.secOther')}</h2>
            <NotifRows items={notifOther} t={t} markDone={markDone} postpone={postpone} />
          </section>
        </>
      )}

      {sub === 'tasks' && (
        <>
          <section className="panel panel--elevated notif-today-block">
            <h2 className="panel__title">{t('notifications.todayTitle')}</h2>
            <p className="panel__muted">{t('notifications.todayIntro')}</p>
            <ol className="notif-today-list">
              {[1, 2, 3, 4, 5].map((i) => (
                <li key={i}>{t(`notifications.today${i}`)}</li>
              ))}
            </ol>
          </section>
          <section className="panel panel--elevated notif-bucket">
            <h2 className="panel__title">{t('notifications.tasksSecCritical')}</h2>
            <TaskRows items={taskCritical} />
          </section>
          <section className="panel panel--elevated notif-bucket">
            <h2 className="panel__title">{t('notifications.tasksSecOverdue')}</h2>
            <TaskRows items={taskOverdue} />
          </section>
          <section className="panel panel--elevated notif-bucket">
            <h2 className="panel__title">{t('notifications.tasksSecUpcoming')}</h2>
            <TaskRows items={taskUpcoming} />
          </section>
          <section className="panel panel--elevated notif-bucket">
            <h2 className="panel__title">{t('notifications.tasksSecIntegration')}</h2>
            <TaskRows items={taskIntegration} />
          </section>
          <section className="panel panel--elevated notif-bucket">
            <h2 className="panel__title">{t('notifications.tasksSecOther')}</h2>
            <p className="panel__muted text-sm">{t('notifications.tasksSecOtherHint')}</p>
            <TaskRows items={taskOther} />
          </section>
        </>
      )}

      <section className="panel panel--elevated notif-pre-client">
        <h2 className="panel__title">{t('notifications.preClientTitle')}</h2>
        <p className="panel__muted text-sm">{t('notifications.preClientLead')}</p>
        <ol className="notif-pre-client-list">
          {PRE_CLIENT_KEYS.map((k) => (
            <li key={k}>{t(`notifications.${k}`)}</li>
          ))}
        </ol>
      </section>

    </div>
  );
}
